import type { EventHandler } from 'svelte/elements'
import { z, ZodError, type output } from 'zod'
import { get, writable } from 'svelte/store'
import { AxiosError } from 'axios'
import i18n from '../i18n'

type NestedKeyOf<ObjectType extends object>
  = { [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`
  }[keyof ObjectType & (string | number)]

export const validatedForm = <
  SchemaType extends z.ZodObject,
  FunctionReturnType,
>({
  zodSchema,
  submitFunction,
  onSuccess,
  onError,
  handleAxiosValidationError = false,
}: {
  zodSchema: SchemaType
  submitFunction: (params: z.infer<SchemaType>) => Promise<FunctionReturnType>
  onSuccess?: (returnData: FunctionReturnType) => void
  onError?: (
    error: Error,
    formValues: Partial<z.infer<SchemaType>>,
    setError: (key: NestedKeyOf<z.infer<SchemaType>>, message: string) => void,
  ) => void
  handleAxiosValidationError?: boolean
}) => {
  const status = writable<'pending' | 'submitting' | 'error' | 'success'>('pending')
  const errors = writable<Partial<z.infer<SchemaType>>>({})
  const formValues = writable<Partial<z.infer<SchemaType>>>({})

  const setError = (path: NestedKeyOf<z.infer<SchemaType>>, errorMessage: string) => {
    errors.set({
      ...get(errors),
      [path]: errorMessage,
    })
  }

  const setValue = (path: NestedKeyOf<z.infer<SchemaType>>, value: unknown) => {
    formValues.set({
      ...get(errors),
      [path]: value,
    })
  }

  const onSubmit: EventHandler<SubmitEvent, HTMLFormElement> = async (e) => {
    e.preventDefault()
    errors.set({})
    status.set('submitting')
    const values = get(formValues)

    try {
      if (zodSchema.parse(values)) {
        const returnData = await submitFunction(values as z.infer<SchemaType>)
        status.set('success')
        onSuccess?.(returnData)
      }
    }
    catch (err) {
      if (err instanceof ZodError) {
        err.issues.forEach((error) => {
          error.path.forEach((path) => {
            setError(path as NestedKeyOf<z.infer<SchemaType>>, error.message)
          })
        })
      }
      else if (handleAxiosValidationError && err instanceof AxiosError && err.response?.status === 422) {
        Object.keys(err.response.data).forEach((key) => {
          const errorKey = `errors.${err.response?.data[key]}`.toLowerCase()
          const i18nStore = get(i18n)

          let translatedError = i18nStore.t(errorKey)

          if (translatedError === errorKey) {
            const fallbackKey: string = `errors.${key}`
            translatedError = i18nStore.t(fallbackKey) !== fallbackKey
              ? i18nStore.t(fallbackKey)
              : err.response?.data[key]
          }

          setError(key as NestedKeyOf<output<SchemaType>>, translatedError)
        })
      }
      else {
        status.set('error')
        onError?.(err as Error, get(formValues), setError)
      }
    }
  }

  return {
    onSubmit,
    formValues,
    errors,
    status,
    setValue,
  }
}
